import React, { useCallback, useEffect, useReducer } from 'react';

import useAsync from 'components/hooks/useAsync';
import { shallowEqual, useSelector } from 'react-redux';
import useLevelUpSidePanelContext from 'screens/oneOnOne/levelUp/competencySidePanel/context';
import { selectLoggedInEmployeeId } from 'selectors/loggedInEmployee';
import { checkLevelUpModuleEnabled } from 'utils/helpers/moduleAccess';

import {
  getLevelUpDetails,
  updateCompetencyTargetDate,
  updateEmployeeTargetRole,
  updateCompetencyProgressStatus,
} from 'services/growth/oneOnOne';

import { LEVELUP_ACTIONS } from './actions';
import { LevelUpProfilePageInitialState, LevelUpProfilePageContext } from './index';
import { levelUpContextReducer } from './reducer';

export const LevelUpProfilePageContextProvider = ({
  children,
  employeeId,
  checkInInstanceId,
  checkInParentId,
  filterEmpIds,
}) => {
  const loggedInEmployeeId = useSelector(selectLoggedInEmployeeId, shallowEqual);

  const {
    state: { closedCount: levelUpSidePanelClosed },
  } = useLevelUpSidePanelContext();

  const { run, isLoading, isIdle } = useAsync();

  const [levelUpState, levelUpDispatch] = useReducer(levelUpContextReducer, LevelUpProfilePageInitialState);

  const setLevelUpDetails = useCallback((levelUpDetails) => {
    levelUpDispatch({
      type: LEVELUP_ACTIONS.SET_LEVEL_UP_DETAILS,
      payload: levelUpDetails,
    });
  }, []);

  const openRoleSelectionModal = useCallback(({ isNewRoleSelection = false } = {}) => {
    levelUpDispatch({
      type: LEVELUP_ACTIONS.OPEN_ROLE_SELECTION_MODAL,
      payload: { isNewRoleSelection },
    });
  }, []);

  const setIsManagerFlag = useCallback((isLoggedInEmployeeManager) => {
    levelUpDispatch({
      type: LEVELUP_ACTIONS.SET_IS_MANAGER_FLAG,
      payload: isLoggedInEmployeeManager,
    });
  }, []);

  const closeRoleSelectionModal = useCallback(() => {
    levelUpDispatch({
      type: LEVELUP_ACTIONS.CLOSE_ROLE_SELECTION_MODAL,
      payload: null,
    });
  }, []);

  const handleCompetencyTargetDateChange = useCallback(
    ({ jobFunctionId, jobRoleId, competencyId, subCompetencyId, targetPeriodStart }) =>
      updateCompetencyTargetDate({
        employeeId,
        jobFunctionId,
        jobRoleId,
        competencyId,
        subCompetencyId,
        targetPeriodStart,
      })
        .then((res) => {
          console.log(res);
          levelUpDispatch({
            type: LEVELUP_ACTIONS.UPDATE_COMPETENCY_TARGET_DATE,
            payload: {
              jobFunctionId,
              jobRoleId,
              competencyId,
              subCompetencyId,
              targetPeriodStart,
            },
          });
        })
        .catch((err) => console.error(err.message || 'failed to update competency target date')),
    [employeeId]
  );

  const handleRoleSelect = useCallback(
    async ({ jobFunctionId, jobRoleId }) =>
      updateEmployeeTargetRole({ employeeId, jobFunctionId, jobRoleId })
        .then((res) => setLevelUpDetails(res))
        .catch((err) => console.error(err.message || 'failed to select role for level up')),
    [employeeId]
  );

  const handleCompetencyProgressStatusChange = useCallback(
    ({ jobFunctionId, jobRoleId, competencyId, subCompetencyId, status }) =>
      updateCompetencyProgressStatus({
        employeeId,
        jobFunctionId,
        jobRoleId,
        competencyId,
        subCompetencyId,
        status,
      })
        .then((res) => {
          levelUpDispatch({
            type: LEVELUP_ACTIONS.UPDATE_COMPETENCY_PROGRESS_STATUS,
            payload: {
              jobFunctionId,
              jobRoleId,
              competencyId,
              subCompetencyId,
              status,
            },
          });
        })
        .catch((err) => console.error(err.message || 'failed to update competency progress status')),
    [employeeId]
  );

  useEffect(() => {
    if (employeeId && checkLevelUpModuleEnabled()) {
      run(
        getLevelUpDetails({ employeeId })
          .then((res) => {
            setLevelUpDetails(res);
            setFunctionAndRoleNotSet(false); // resetting <- to false in case it was true
          })
          .catch((err) => {
            if (err.status === 422) {
              setFunctionAndRoleNotSet(true);
            } else {
              console.error('Error fetching level up competency data : ', err);
            }
          })
      );
    }
  }, [employeeId, levelUpSidePanelClosed]);

  const setFunctionAndRoleNotSet = useCallback((isSet) => {
    levelUpDispatch({
      type: LEVELUP_ACTIONS.SET_FUNCTION_AND_ROLE_NOT_SET,
      payload: { isSet },
    });
  }, []);

  useEffect(() => {
    setIsManagerFlag(String(employeeId) !== String(loggedInEmployeeId));
  }, [employeeId, loggedInEmployeeId]);

  useEffect(() => {
    levelUpDispatch({
      type: LEVELUP_ACTIONS.SET_ONE_ON_ONE_INSTANCE_DETAILS,
      payload: {
        checkInInstanceId,
        checkInParentId,
        filterEmpIds,
      },
    });
  }, [checkInInstanceId, checkInParentId, filterEmpIds]);

  return (
    <LevelUpProfilePageContext.Provider
      value={{
        state: { ...levelUpState, reporteeId: employeeId, isLoading: isLoading || isIdle },
        setLevelUpDetails,
        openRoleSelectionModal,
        closeRoleSelectionModal,
        handleCompetencyTargetDateChange,
        handleRoleSelect,
        handleCompetencyProgressStatusChange,
      }}
    >
      {children}
    </LevelUpProfilePageContext.Provider>
  );
};
